import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Calendar, Clock, MapPin, CurrencyGbp, Users, ClipboardText } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import JobMap from "@/components/JobMap";
import { fileUrl } from "@/lib/api";

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [booking, setBooking] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/jobs/${id}`);
      const branding = await api.get(`/company/${r.data.job.company_id}/branding`).then(x => x.data).catch(() => null);
      setData({ ...r.data, branding });
    } catch { toast.error("Job not found"); }
  };
  useEffect(() => { load(); }, [id]);

  const book = async () => {
    setBooking(true);
    try {
      await api.post(`/jobs/${id}/book`);
      toast.success("Shift booked! You'll receive a confirmation.");
      nav("/staff/bookings");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Booking failed"));
    } finally { setBooking(false); }
  };

  if (!data) return <div className="p-6 label-caps">Loading…</div>;
  const { job, my_booking } = data;
  const remaining = Math.max(0, (job.staff_required || 0) - (job.staff_booked || 0));
  const brand = data.branding?.hero_color || "#2563EB";
  const logo = data.branding?.logo;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {(logo || data.branding?.hero_color) && (
        <div className="card-tactile overflow-hidden">
          <div className="h-24 relative" style={{ background: `linear-gradient(135deg, ${brand}, ${brand}CC)` }} data-testid="job-brand-header">
            {logo && (
              <div className="absolute bottom-3 left-4 w-16 h-16 bg-white rounded-md border-2 border-white shadow overflow-hidden">
                <img src={fileUrl(logo)} alt="brand" className="w-full h-full object-contain" data-testid="job-brand-logo" />
              </div>
            )}
            <div className={`absolute bottom-3 ${logo ? "left-24" : "left-5"} text-white`}>
              <div className="font-display text-lg font-extrabold">{job.company_name}</div>
              <div className="text-white/80 text-xs">Presented shift</div>
            </div>
          </div>
        </div>
      )}
      <div className="card-tactile p-6">
        <div className="label-caps text-blue-600 mb-2">{job.category}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">{job.title}</h1>
        <div className="text-slate-600 mt-1 mb-5">{job.company_name}</div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Row icon={Calendar} label="Date" value={job.date} />
          <Row icon={Clock} label="Time" value={`${job.start_time} – ${job.end_time}`} />
          <Row icon={MapPin} label="Location" value={`${job.location}${job.postcode ? " · " + job.postcode : ""}`} />
          <Row icon={CurrencyGbp} label="Pay rate" value={`£${job.pay_rate}/hr`} />
          <Row icon={Users} label="Positions left" value={`${remaining} of ${job.staff_required}`} />
          <Row icon={ClipboardText} label="Break" value={`${job.break_minutes} min`} />
        </div>
        <div className="mt-6">
          <div className="label-caps mb-1">Description</div>
          <p className="text-slate-800 leading-relaxed">{job.description}</p>
        </div>
        {(job.required_skills?.length || 0) > 0 && (
          <div className="mt-4">
            <div className="label-caps mb-2">Required skills</div>
            <div className="flex flex-wrap gap-2">
              {job.required_skills.map((s) => <span key={s} className="px-2.5 py-1 bg-slate-100 rounded-md text-sm font-semibold">{s}</span>)}
            </div>
          </div>
        )}
        {job.dress_code && <div className="mt-4"><span className="label-caps mr-2">Dress code:</span><span className="text-slate-800">{job.dress_code}</span></div>}
        {job.instructions && <div className="mt-2"><span className="label-caps mr-2">Instructions:</span><span className="text-slate-800">{job.instructions}</span></div>}
      </div>

      <JobMap jobId={job.id} postcode={job.postcode} location={job.location} />

      <div className="sticky bottom-20 md:bottom-4 z-30">
        {my_booking ? (
          <div className="card-tactile p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-display text-lg font-bold text-slate-900">You've booked this shift</div>
              <div className="text-sm text-slate-600">See it under your bookings.</div>
            </div>
            <StatusBadge status={my_booking.status} />
          </div>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="book-shift-btn" disabled={remaining === 0}
                className="w-full h-14 bg-blue-600 hover:bg-blue-700 rounded-md font-semibold text-lg">
                {remaining === 0 ? "No positions available" : "Book this shift"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm booking</AlertDialogTitle>
                <AlertDialogDescription>
                  You're booking <strong>{job.title}</strong> on <strong>{job.date}</strong> from <strong>{job.start_time}–{job.end_time}</strong>. You can cancel later according to the cancellation policy.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="book-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="book-confirm-btn" onClick={book} disabled={booking} className="bg-blue-600 hover:bg-blue-700">
                  {booking ? "Booking…" : "Yes, book shift"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5 p-3 bg-slate-50 rounded-md">
      <Icon size={18} className="text-blue-600 mt-0.5" />
      <div>
        <div className="label-caps">{label}</div>
        <div className="text-slate-900 font-semibold">{value}</div>
      </div>
    </div>
  );
}
